/**
 * CoPilot schemas — boundary validation for guide-state messages
 * and brain-served guide configs.
 *
 * These exist to close the type-system holes that the four-layer
 * radar (2026-04-28) flagged:
 *   • C-3 / M-9 — `chat_messages` was being cast through
 *     `as unknown as IMessage[]` between server JSON and the
 *     frontend `IMessage` type. A Zod schema validated at the
 *     boundary catches drift instead of letting it explode at
 *     render time.
 *   • H-5 — trace payload casts. The trace payload is JSONB; we
 *     don't fully validate it, but the wire shape (input / output
 *     / reasoning) gets a typed schema here.
 *   • M-3 — brain-served guide configs are opaque
 *     `Record<string, unknown>`. The proxy now has the option to
 *     validate against `guideConfigSchema` before handing to the
 *     frontend.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────
// CHAT MESSAGES (UI bubbles persisted on copilot_guide_states.chat_messages)
// ─────────────────────────────────────────────────────────────────

/**
 * Wire shape of a single rendered chat bubble. The frontend
 * `IMessage` type carries `timestamp: Date`, but the wire format
 * is ISO-8601 (JSON has no Date). Validation accepts either; the
 * frontend hydrates `Date` after validation.
 */
export const chatMessageWireSchema = z
  .object({
    id: z.string(),
    /**
     * Authorship. Aligned with `IMessage.type` in journey-web's
     * guide types — `bot` for Tonnie, `user` for the citizen. We
     * intentionally do NOT use `assistant`/`system` here: those are
     * brain-side LLM-message tokens, not user-visible chat bubbles.
     * The persisted `chat_messages` JSONB column stores rendered
     * bubbles, so the wire shape must match the renderer's contract.
     */
    type: z.enum(['bot', 'user']),
    content: z.string(),
    /**
     * ISO-8601 string OR a Date (when read from in-memory state).
     * The persistence layer always serialises to string before
     * storing.
     */
    timestamp: z.union([z.string(), z.date()]),
    /** Free-form metadata (e.g. trace correlation id). Bounded loosely. */
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  // Allow forward-compatible additions (e.g. a future `attachments`
  // field) without breaking existing readers — and to admit the
  // optional `IMessage` fields (`isTyping`, `taskConfig`, `options`,
  // `userResponse`, `questionType`, `isCompletionMessage`) without
  // re-declaring them here.
  .loose();

export const chatMessagesWireSchema = z.array(chatMessageWireSchema);

export type ChatMessageWire = z.infer<typeof chatMessageWireSchema>;
export type ChatMessagesWire = z.infer<typeof chatMessagesWireSchema>;

// ─────────────────────────────────────────────────────────────────
// GUIDE CONFIG (brain-served per-flow JSON)
// ─────────────────────────────────────────────────────────────────

/**
 * Minimal guide-config shape we contract against. The `tasks`
 * array is the structural part the host page reads (e.g. to
 * compute `totalSteps` for the resume modal). Inner shapes are
 * permissive (`passthrough`) so the brain team can extend without
 * coordinating a SDK release every time.
 */
export const guideTaskSchema = z
  .object({
    id: z.string(),
    type: z.string(), // 'Question' | 'Form' | 'Terminal' | 'Escape' | …
    chats: z.array(z.unknown()).optional(),
  })
  .loose();

export const guideConfigSchema = z
  .object({
    id: z.string(),
    /** Semantic version, e.g. "1.0.0" or "v2026.02.28.1200". */
    version: z.string().optional(),
    /** Optional flow-id; brain may omit if it equals `id`. */
    flowId: z.string().optional(),
    content: z
      .object({
        tasks: z.array(guideTaskSchema).optional(),
        entryChat: z.string().optional(),
      })
      .loose()
      .optional(),
  })
  .loose();

export type GuideTask = z.infer<typeof guideTaskSchema>;
export type GuideConfig = z.infer<typeof guideConfigSchema>;

// ─────────────────────────────────────────────────────────────────
// TRACE PAYLOADS (wire shape of CoPilotTrace `input`/`output`/`reasoning` JSONB)
// ─────────────────────────────────────────────────────────────────

export const traceInputSchema = z.record(z.string(), z.unknown());
export const traceOutputSchema = z.record(z.string(), z.unknown());
export const traceReasoningSchema = z.record(z.string(), z.unknown());

export type TraceInput = z.infer<typeof traceInputSchema>;
export type TraceOutput = z.infer<typeof traceOutputSchema>;
export type TraceReasoning = z.infer<typeof traceReasoningSchema>;

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

/**
 * Validate a server-returned `chat_messages` payload. Returns the
 * parsed array on success; on failure logs a warning and falls
 * back to an empty array — the engine fresh-starts rather than
 * crashing on a single malformed message.
 *
 * Used by `use-co-pilot-guide.ts` restore branch; replaces the
 * previous `as unknown as IMessage[]` cast (radar C-3).
 */
export function safeParseChatMessages(value: unknown): ChatMessagesWire {
  const result = chatMessagesWireSchema.safeParse(value);
  if (result.success) return result.data;

  console.warn('[CP:Schema] chat_messages payload failed validation', {
    issues: result.error.issues.slice(0, 3),
  });
  return [];
}
